import React, { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { resolvePostLoginRoute } from '../orbit/lib/postLoginRoute';
import { clearAuthState } from '../services/authStorage';

const OAuthCallback: React.FC = () => {
    const navigate = useNavigate();
    const [queryParams] = useSearchParams();

    useEffect(() => {
        // Auth data arrives in the URL FRAGMENT (#token=...) — fragments never
        // reach servers, logs, or the Referer header. Query kept as fallback
        // for any in-flight redirect minted by an older backend.
        const fragment = new URLSearchParams((window.location.hash || '').replace(/^#/, ''));
        const searchParams = fragment.get('token') ? fragment : queryParams;
        const token = searchParams.get('token');
        const error = searchParams.get('error');

        if (error) {
            toast.error('OAuth sign-in failed. Please try again.');
            navigate('/login');
            return;
        }

        if (!token) {
            toast.error('No authentication token received.');
            navigate('/login');
            return;
        }

        const role = searchParams.get('role') || 'client';
        const clientId = searchParams.get('client_id');
        // The backend sets is_new=true ONLY when this Google sign-in just
        // CREATED the account (a signup). An existing registered email signs in
        // with is_new=false → lands on /dashboard, not the onboarding surface.
        const isNew = searchParams.get('is_new') === 'true';

        // Store auth data — same as Login.tsx handleSubmit.
        // clearAuthState() only drops auth-session keys; domain data
        // (orbit:turns:*, orbit:active-turn-idx:*, etc.) survives so
        // returning users keep their chat history + pane position.
        clearAuthState();
        localStorage.setItem('auth_token', token);
        const refreshToken = searchParams.get('refresh_token');
        if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
        localStorage.setItem('user_role', role);
        localStorage.setItem('user_id', searchParams.get('user_id') || '');
        localStorage.setItem('user_email', searchParams.get('email') || '');
        localStorage.setItem('user_name', searchParams.get('name') || '');
        if (clientId) localStorage.setItem('client_id', clientId);
        const consultantId = searchParams.get('consultant_id');
        if (consultantId) localStorage.setItem('consultant_id', consultantId);

        toast.success('Login successful!');

        // First-time signup → /orbit/intake; returning login → /dashboard (the
        // dashboard surfaces any pending intake as a to-do). `is_new` is the
        // authoritative signal — never bounce a returning user into onboarding.
        resolvePostLoginRoute(role, clientId, isNew).then(target => {
            window.location.href = target;
        });
    }, [queryParams, navigate]);

    return (
        <div className="min-h-screen flex items-center justify-center bg-white">
            <div className="text-center">
                <div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                <p className="text-sm text-gray-500">Signing you in...</p>
            </div>
        </div>
    );
};

export default OAuthCallback;
